import React, { CSSProperties } from 'react';
import useStyles from './styles';
import ToolbarFullPageContainer from 'components/app_common/ToolbarFullpageContainer';
import Divider from '@material-ui/core/Divider';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { Box, Button, Paper, Typography, PaperProps } from '@material-ui/core';
import { useHistory, useLocation } from 'react-router';
import * as PathUtils from 'utils/path';
import classNames from 'classnames';
import { TranslatedTextV2 } from 'components/atoms/TranslatedText';

export interface IBaseMenu {
  icon?: React.ReactNode;
  iconRight?: React.ReactNode;
  label?: React.ReactNode;
  id: string;
  path?: string;
  onClick?: () => void;
}

export interface IMenu extends IBaseMenu {
  overrideItem?: (props: IBaseMenu) => React.ReactNode;
}

interface IMenuContainer {
  Header?: React.ReactElement;
  Footer?: React.ReactElement;
  menu: IMenu[];
}

export interface BaseNavigationMenuProps {
  menuContainer: IMenuContainer | IMenuContainer[];
  profile?: {};
  Header?: React.ReactNode;
  Footer?: React.ReactNode;
  handleGoBack?: () => void;
  topDividerVisibility?: 'visible' | 'invisible';
  dividerVisibility?: 'visible' | 'invisible';
  containerProps?: Omit<PaperProps, 'children'>;
  style?: CSSProperties;
}

const TOOLBAR_MAX_HEIGHT = '64px';
const SIDE_MENU_WIDTH = 320;

const BaseNavigationMenu = ({
  menuContainer,
  Header,
  Footer,
  handleGoBack,
  topDividerVisibility = 'visible',
  dividerVisibility = 'visible',
  containerProps,
}: BaseNavigationMenuProps) => {
  const classes = useStyles();
  const history = useHistory();

  const drawer = (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      {handleGoBack && (
        <Box display="flex" alignItems="center" padding="0.1rem">
          <Button onClick={handleGoBack} style={{ textTransform: 'none' }}>
            <ArrowBackIcon style={{ marginRight: '1rem' }} />
            <Typography
              style={{
                fontWeight: 500,
                color: 'rgba(130, 133, 133)',
                fontSize: '1rem',
              }}
            >
              <TranslatedTextV2
                defaultText="Back"
                translationKey="base_navigation_menu.back"
              />
            </Typography>
          </Button>
        </Box>
      )}
      {Header}
      {topDividerVisibility === 'visible' && <Divider />}
      <div style={{ height: '100%' }}>
        {Array.isArray(menuContainer) ? (
          menuContainer.map(({ menu, Footer, Header }, index) => (
            <div key={index}>
              <List subheader={Header}>
                {menu.map((data, index) => (
                  <MenuItem {...data} key={data.id} />
                ))}
              </List>
              <div>{Footer}</div>
              {dividerVisibility && <Divider />}
            </div>
          ))
        ) : (
          <React.Fragment>
            <List subheader={menuContainer.Header}>
              {menuContainer.menu.map((data) => (
                <MenuItem {...data} key={data.id} />
              ))}
            </List>
            <div>{menuContainer.Footer}</div>
          </React.Fragment>
        )}
      </div>
      {Footer}
    </div>
  );

  return (
    <Paper
      square
      variant="outlined"
      {...containerProps}
      style={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        ...containerProps?.style,
      }}
    >
      {drawer}
    </Paper>
  );
};

export const MenuItem = (props: IMenu) => {
  const location = useLocation();
  const classes = useStyles();
  const history = useHistory();
  const selected = props.path
    ? PathUtils.pathsIsTheSame(location.pathname, props.path)
    : false;
  const { overrideItem, onClick, ...itemProps } = props;

  return (
    <React.Fragment>
      <ListItem
        onClick={() => {
          onClick && onClick();
          props.path && history.push(props.path);
        }}
        // classes={{ selected: classes.active }}
        selected={selected}
        className={classes.listItem}
        button
      >
        {overrideItem ? (
          overrideItem(itemProps)
        ) : (
          <React.Fragment>
            {props.icon && (
              <ListItemIcon
                className={classNames({
                  [classes.active]: selected,
                  [classes.listItemIcon]: true,
                })}
              >
                {props.icon}
              </ListItemIcon>
            )}
            {props.label && (
              <ListItemText
                className={classNames({
                  [classes.active]: selected,
                  [classes.listItemText]: true,
                })}
                primary={props.label}
              />
            )}
            {props.iconRight && (
              <ListItemIcon
                className={classNames({
                  [classes.active]: selected,
                  [classes.listItemIcon]: true,
                })}
                style={{ minWidth: 0 }}
              >
                {props.iconRight}
              </ListItemIcon>
            )}
          </React.Fragment>
        )}
      </ListItem>
    </React.Fragment>
  );
};

export const BaseNavigationMenuContainer = ({
  style,
  ...props
}: BaseNavigationMenuProps) => {
  return (
    <ToolbarFullPageContainer
      style={{
        width: SIDE_MENU_WIDTH,
        position: 'sticky',
        top: TOOLBAR_MAX_HEIGHT,
        overflowY: 'auto',
        ...style,
      }}
    >
      <div style={{ height: '100%', width: '100%' }}>
        <BaseNavigationMenu {...props} />
      </div>
    </ToolbarFullPageContainer>
  );
};

export default React.memo(BaseNavigationMenu);
